// Turns the central usage log (rows from the `usage_events` table) into what
// the Admin Dashboard shows. Pure functions, so they are easy to test.

const COUNTERS = {
  bible_search: 'bibleSearches',
  song_search: 'songSearches',
  song_displayed: 'songsDisplayed',
  verse_displayed: 'versesDisplayed',
  voice_search_used: 'voiceSearchUsed',
  ai_song_search_used: 'aiSongSearchUsed',
  display_window_opened: 'displayWindowOpened',
  settings_changed: 'settingsChanged',
};

const top = (counts, limit = 10) => Object.entries(counts)
  .sort((a, b) => b[1] - a[1])
  .slice(0, limit)
  .map(([name, count]) => ({ name, count }));

const weekKey = (date) => {
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const week = Math.ceil(((date - yearStart) / 86400000 + yearStart.getDay() + 1) / 7);
  return `${date.getFullYear()}-W${String(week).padStart(2, '0')}`;
};

/**
 * Same shape the dashboard tabs already read from analyticsService.getStatistics().
 * `rows` are usage_events rows; only kind 'event' rows count towards these totals.
 */
export function computeStatistics(rows) {
  const events = (Array.isArray(rows) ? rows : [])
    .filter(r => r && r.kind === 'event')
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));

  const statistics = Object.fromEntries(Object.values(COUNTERS).map(k => [k, 0]));
  const popularVerses = {};
  const popularSongs = {};
  const featureUsage = {};
  const daily = {};
  const weekly = {};
  const monthly = {};
  const sessions = [];
  let totalSessions = 0;

  events.forEach((e) => {
    const data = e.data || {};
    if (COUNTERS[e.name]) statistics[COUNTERS[e.name]] += 1;
    if (e.name === 'song_displayed' && data.title) popularSongs[data.title] = (popularSongs[data.title] || 0) + 1;
    if (e.name === 'verse_displayed' && data.reference) popularVerses[data.reference] = (popularVerses[data.reference] || 0) + 1;
    if (e.name === 'app_launched') {
      totalSessions += 1;
      sessions.push({
        sessionId: e.session_id || `row-${e.id}`,
        startTime: e.created_at,
        userAgent: data.userAgent || '',
        platform: data.platform || '',
      });
    }
    featureUsage[e.name] = (featureUsage[e.name] || 0) + 1;

    const date = new Date(e.created_at);
    if (!Number.isNaN(date.getTime())) {
      const day = date.toISOString().split('T')[0];
      const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      daily[day] = { count: (daily[day] ? daily[day].count : 0) + 1, events: [] };
      const w = weekKey(date);
      weekly[w] = { count: (weekly[w] ? weekly[w].count : 0) + 1 };
      monthly[month] = { count: (monthly[month] ? monthly[month].count : 0) + 1 };
    }
  });

  const recentEvents = events.slice(-100).reverse().map(e => ({
    id: `event_${e.id}`,
    name: e.name,
    timestamp: e.created_at,
    sessionId: e.session_id,
    data: e.data || {},
  }));

  return {
    overview: {
      totalSessions,
      totalEvents: events.length,
      avgEventsPerSession: totalSessions > 0 ? (events.length / totalSessions).toFixed(2) : 0,
    },
    statistics,
    popularVerses: top(popularVerses),
    popularSongs: top(popularSongs),
    featureUsage,
    recentActivity: { daily, weekly, monthly },
    recentEvents,
    sessions: sessions.slice(-50).reverse(),
  };
}

export const SESSION_IDLE_MS = 6 * 60 * 60 * 1000; // a session with no logout and no activity for this long counts as ended

/**
 * Login sessions for the "User Sessions" tab, newest first, from kind 'session'
 * (login / logout) and 'activity' rows. `users` are the profile rows (to name
 * each login); a session's user_id is the Supabase auth user id.
 */
export function buildSessions(rows, users = [], now = Date.now()) {
  const byAuth = new Map(users.map(u => [u.authUserId, u]));
  const sessions = new Map();

  const ordered = (Array.isArray(rows) ? rows : [])
    .filter(r => r && (r.kind === 'session' || r.kind === 'activity') && r.session_id)
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));

  ordered.forEach((r) => {
    const profile = byAuth.get(r.user_id);
    let s = sessions.get(r.session_id);
    if (!s) {
      s = {
        id: r.session_id,
        userId: profile ? profile.id : r.user_id,
        username: (profile && profile.username) || (r.data && r.data.username) || 'Unknown',
        loginCode: (profile && profile.loginCode) || (r.data && r.data.loginCode) || '',
        loginTime: r.created_at,
        logoutTime: null,
        userAgent: '',
        platform: '',
        status: 'active',
        activities: [],
        lastSeen: r.created_at,
      };
      sessions.set(r.session_id, s);
    }
    s.lastSeen = r.created_at;
    if (r.kind === 'session' && r.name === 'login') {
      s.loginTime = r.created_at;
      s.userAgent = (r.data && r.data.userAgent) || s.userAgent;
      s.platform = (r.data && r.data.platform) || s.platform;
    } else if (r.kind === 'session' && r.name === 'logout') {
      s.logoutTime = r.created_at;
    } else if (r.kind === 'activity') {
      s.activities.push({ type: r.name, details: r.data || {}, timestamp: r.created_at });
    }
  });

  return [...sessions.values()]
    .map(({ lastSeen, ...s }) => ({
      ...s,
      status: s.logoutTime || now - Date.parse(lastSeen) > SESSION_IDLE_MS ? 'ended' : 'active',
    }))
    .sort((a, b) => Date.parse(b.loginTime) - Date.parse(a.loginTime));
}
